import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import styles from "./GlobalLayoutSettingsPage.module.scss";
import { useTranslation } from "react-i18next";
import { EditLayoutsPage } from "../../../components/Pages/EditLayoutsPage";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer, useLocalObservable } from "mobx-react-lite";
import { modalService } from "@whatslly/ui-components/modals";
import { MessageModal } from "@whatslly/ui-components/modals/MessageModal";
import type { IAdminLayoutFieldsDataLayer, LayoutFieldsFilter } from "../../types/ILayoutFieldsProvider";
import { LayoutFieldsProviderSymbol } from "../../stores/LayoutFieldsProviderSymbol";
import { AsyncSelect } from "@whatslly/ui-components/v2/BaseElements/Select";
import { CrmComponentsErrorBox } from "@whatslly/ui-components/v2/CrmComponentsErrorBox";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { fromPromise } from "mobx-utils";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { i18n } from "@whatslly/commons/i18nService";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { Tooltip } from "@whatslly/ui-components/Tooltip";

export const SHOW_GLOBAL_LAYOUT_FOR_ALL_PROFILES = undefined;

const GlobalLayoutSettingsHeader: React.FC<{
  setProfileName: Dispatch<SetStateAction<string | undefined>>;
  profileName: string | undefined;
  showGlobalLayoutPerProfile: boolean;
  showResetUserLayout: boolean;
  children?: React.ReactNode;
}> = observer(({ profileName, setProfileName, showGlobalLayoutPerProfile, children, showResetUserLayout }) => {
  const { t } = useTranslation();
  const layoutProviderStore = useDependencyInjector<IAdminLayoutFieldsDataLayer>(LayoutFieldsProviderSymbol);

  const [loading, setLoading] = useState(false);
  const handleToggleHideLayout = useCallback(async () => {
    setLoading(true);
    const res = await layoutProviderStore.toggleHideEditLayout();
    if (!res.success) {
      modalService.openModal(MessageModal, {
        children: <span data-testid={`failed-to-update-hide-layout-modal`}>{t("globalLayout.failed_to_update_hide_edit_layout")}</span>,
      });
    }
    setLoading(false);
  }, [layoutProviderStore, t]);

  const handleResetUserLayouts = useCallback(async () => {
    const confirm = await modalService.openModal(ConfirmModal, {
      title: i18n.t("globalLayout.reset_user_layout"),
      body: i18n.t("globalLayout.reset_user_layout_confirm"),
    }).promise;
    if (!confirm?.result) return;
    const res = await layoutProviderStore.resetUserLayouts();
    if (res.success) {
      await modalService.openModal(Toast, { text: i18n.t("globalLayout.reset_user_layout_success") }).promise;
    } else {
      await modalService.openModal(Toast, { type: "error", text: i18n.t("globalLayout.reset_user_layout_failure") }).promise;
    }
  }, [layoutProviderStore]);

  return (
    <div className={styles.header}>
      <div className={styles.headerTitles}>
        <div className={styles.title} data-testid={"GlobalLayoutTitle"}>
          {t("globalLayout.title")}
        </div>
        <div className={styles.subTitle}>{t("editLayouts.editing_the_global_layout_affects_all_records")}</div>
      </div>
      <div className={styles.headerActions}>
        {showGlobalLayoutPerProfile && (
          <div className={styles.globalLayoutPerProfileSelectContainer}>
            <AsyncSelect
              loadOptionsFromServerFunction={async (input) => {
                const defaultOption = { options: [{ label: t("record_type_layout.default"), value: SHOW_GLOBAL_LAYOUT_FOR_ALL_PROFILES }] };

                const profilesNames = await layoutProviderStore.getProfilesNames(input);
                if (!profilesNames || !Array.isArray(profilesNames)) return defaultOption;

                const profilesNamesOptions = profilesNames.map((profileName) => ({ label: profileName, value: profileName }));
                return {
                  options: [...defaultOption.options, ...profilesNamesOptions],
                };
              }}
              isSearchable={false}
              value={{
                label: profileName || t("record_type_layout.default"),
                value: profileName || SHOW_GLOBAL_LAYOUT_FOR_ALL_PROFILES,
              }}
              defaultValue={{ label: t("record_type_layout.default"), value: SHOW_GLOBAL_LAYOUT_FOR_ALL_PROFILES }}
              onChange={(val: { label: string; value: string }) => setProfileName(val.value)}
              data-testid={"global-layout-per-profile-select"}
            />
          </div>
        )}
        {children}
        {showResetUserLayout && (
          <Tooltip delay={300} value={t("globalLayout.reset_user_layout")} placement={"bottom"}>
            <div data-testid={`reset-user-layouts`} onClick={handleResetUserLayouts} className={styles.headerButton}>
              <SvgV2 width={12} height={12} iconName={"close"} />
            </div>
          </Tooltip>
        )}
        <label className={styles.hideOrShowEditLayoutButton} data-testid={`hide-edit-layout-button`}>
          <CheckBox
            disabled={loading || layoutProviderStore.isReadonly}
            checked={layoutProviderStore.hideEditLayout}
            onChange={handleToggleHideLayout}
          />
          <span className={styles.buttonText}>{t("globalLayout.hide_edit_layout")}</span>
        </label>
      </div>
    </div>
  );
});

export const GlobalLayoutSettingsPage: React.FC<{
  hideAddFields?: boolean;
  showAddRawField?: boolean;
  showEditButton?: boolean;
  headerChildren?: React.ReactNode;
  filter?: LayoutFieldsFilter;
}> = observer(({ hideAddFields, showAddRawField, showEditButton, headerChildren, filter }) => {
  const [profileName, setProfileName] = useState<string | undefined>();
  const layoutProviderStore = useDependencyInjector<IAdminLayoutFieldsDataLayer>(LayoutFieldsProviderSymbol);
  const { t } = useTranslation();

  const localStore = useLocalObservable(() => ({
    get init() {
      return fromPromise(layoutProviderStore.init());
    },
  }));

  return (
    <>
      {localStore.init.case({
        rejected: (error: Error) => <CrmComponentsErrorBox error={error.message} />,
        pending: () => (
          <div className={styles.wrapper}>
            <div className={styles.pageLoader}>
              <img alt="tuvis loader" src={LOADER_IMG} />
              <div>{t("autoSyncActivitiesPage.loading")}</div>
            </div>
          </div>
        ),
        fulfilled: ({ showGlobalLayoutPerProfile, showResetUserLayout }) => (
          <div className={styles.wrapper}>
            <GlobalLayoutSettingsHeader
              profileName={profileName}
              setProfileName={setProfileName}
              showGlobalLayoutPerProfile={showGlobalLayoutPerProfile}
              showResetUserLayout={showResetUserLayout}
            >
              {headerChildren}
            </GlobalLayoutSettingsHeader>
            <div className={styles.container}>
              <EditLayoutsPage
                initializeValue={undefined}
                pageClassName={styles.editLayoutPage}
                footerClassName={styles.footerClassName}
                hideCloseAndRefreshButton
                mainWrapperClassName={styles.editLayoutPageMainWrapper}
                loaderClassName={styles.loaderClassName}
                hideAddFields={hideAddFields}
                showAddRawField={showAddRawField}
                showEditButton={showEditButton}
                readOnly={layoutProviderStore.isReadonly}
                filter={{ profileName, ...(filter || {}) }}
              />
            </div>
          </div>
        ),
      })}
    </>
  );
});
